import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import useGoBack from '../hooks/useGoBack'
import {
  Form,
  Input,
  InputNumber,
  Button,
  Upload,
  Select,
  Card,
  message,
  Breadcrumb,
  Switch,
  Popconfirm,
  Spin,
  Row,
  Col,
  DatePicker,
  Modal
} from 'antd'
import {
  PlusOutlined,
  ArrowLeftOutlined,
  UploadOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import {
  getInventoryProduct,
  createInventoryProduct,
  updateInventoryProduct,
  getInventoryCategories
} from '../api/inventory'
import { PHOTO_URL } from '../utils/apiutils'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import api from '../utils/api'
import InventoryCategoryForm from '../components/inventory/InventoryCategoryForm'

const { Option } = Select
const { useWatch } = Form

const PageContainer = styled.div`
  padding: 24px;
  background: #f0f2f5;
  min-height: 100vh;

  @media screen and (max-width: 768px) {
    padding: 16px;
  }
`

const HeaderSection = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const ContentCard = styled(Card)`
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: none;
`

const StyledForm = styled(Form)`
  margin: 0 auto;
`

const ImagePreview = styled.div`
  .ant-upload-list-picture-card {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  .ant-upload-list-picture-card .ant-upload-list-item {
    margin: 0 !important;
  }

  .ant-upload.ant-upload-select-picture-card {
    margin: 0 !important;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
  padding: 24px 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  @media screen and (max-width: 768px) {
    flex-direction: column;
    padding: 16px;

    button {
      width: 100%;
    }
  }
`

const PriceGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const SubtotalDisplay = styled.div`
  grid-column: 1 / -1;
  padding: 16px;
  background: #fafafa;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;

  .subtotal-label {
    font-size: 14px;
    color: #6b7280;
    font-weight: 500;
  }

  .subtotal-value {
    font-size: 18px;
    color: #1a1a1a;
    font-weight: 600;
  }
`

const StatusSwitch = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  padding: 16px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`

const StatusText = styled.span`
  color: ${(props) => (props.isActive ? '#52c41a' : '#ff4d4f')};
  font-weight: 500;
`

const AddInventoryProductPage = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const location = useLocation()
  // 這頁可能從庫存商品列表，也可能從線上商店的商品列表進來。
  // 沒有站內歷史時（重整、直接貼網址）要退回「進來時的那個列表」，
  // 不能一律寫死 /inventory/products，否則從商店進來的人會被丟到庫存管理。
  const backTo = location.state?.from || '/inventory/products'
  const goBack = useGoBack(backTo)
  const { productId } = useParams()
  const { businessId } = useAuth()
  const { can } = usePermission()
  const [loading, setLoading] = useState(false)
  const [isActive, setIsActive] = useState(true)
  const [hasChanges, setHasChanges] = useState(false)
  const [categories, setCategories] = useState([])
  const [initialData, setInitialData] = useState(null)
  const [fetching, setFetching] = useState(false)
  const [employees, setEmployees] = useState([])
  const [deletedImages, setDeletedImages] = useState([]) // 跟踪已删除的图片 URL
  const [categoryModalOpen, setCategoryModalOpen] = useState(false)
  const { hasFeature } = useMembership()

  const canViewFinance = can('finance:view')

  // 如果是编辑模式但没有财务权限，重定向到商品列表
  useEffect(() => {
    if (productId && !canViewFinance) {
      message.warning('您沒有權限編輯商品，因為編輯頁面包含財務信息')
      navigate(backTo, { replace: true })
    }
  }, [productId, canViewFinance, navigate, backTo])

  // 使用 ref 來防止無限循環
  const initializedRef = useRef(false)
  const loadingRef = useRef(false)

  // 監聽是否同時進貨的選項
  const enablePurchase = Form.useWatch('enablePurchase', form)

  // 監聽庫存數量和成本，用於計算小計和實際付款金額
  const stock = Form.useWatch('stock', form)
  const cost = Form.useWatch('cost', form)

  // ── 庫存以容量計價 ──
  // capacity 是自由文字，這裡的解析規則必須與後端 variantStock.parseCapacity
  // 一致：抓字串裡第一個數字，不校驗單位。
  const capacityValue = Form.useWatch('capacity', form)
  const stockByCapacity = Form.useWatch('stockByCapacity', form)
  const parsedUnitSize = (() => {
    if (capacityValue == null) return null
    const m = String(capacityValue).match(/\d+(?:\.\d+)?/)
    if (!m) return null
    const n = Number(m[0])
    return Number.isFinite(n) && n > 0 ? n : null
  })()
  const sellableUnits =
    parsedUnitSize && Number(stock) > 0
      ? Math.floor(Number(stock) / parsedUnitSize)
      : 0

  // 計算小計
  const subtotal = (Number(stock) || 0) * (Number(cost) || 0)

  // 編輯模式下，若庫存被改動，需填寫「調整原因」並寫入異動明細
  const stockChanged =
    !!productId &&
    !!initialData &&
    stock !== undefined &&
    stock !== null &&
    Number(stock) !== Number(initialData.stock)

  // 當小計變化時，自動更新實際付款金額
  useEffect(() => {
    if (enablePurchase) {
      form.setFieldsValue({
        purchaseActualAmount: subtotal
      })
    }
  }, [subtotal, enablePurchase, form])

  const fetchCategories = useCallback(async () => {
    try {
      const res = await getInventoryCategories()
      const list =
        res.data?.data || (Array.isArray(res.data) ? res.data : []) || []
      setCategories(list)
      return list
    } catch (error) {
      setCategories([])
      return []
    }
  }, [])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  const handleCategoryCreated = async () => {
    const hadNone = categories.length === 0
    setCategoryModalOpen(false)
    const list = await fetchCategories()
    if (hadNone && list.length > 0) {
      const last = list[list.length - 1]
      const id = last._id || last.id
      if (id) form.setFieldsValue({ categoryId: id })
    }
  }

  // 獲取員工列表（用於進貨功能，只在新增商品時獲取）
  const fetchEmployees = useCallback(async () => {
    if (!businessId || productId || loadingRef.current) return
    if (!hasFeature('inventory')) return
    loadingRef.current = true
    try {
      const response = await api.get(`/api/employees/${businessId}`)
      const employeesData = Array.isArray(response.data)
        ? response.data
        : response.data?.data || []
      setEmployees(employeesData)
    } catch (error) {
      console.error('獲取員工列表失敗:', error)
      setEmployees([])
    } finally {
      loadingRef.current = false
    }
  }, [businessId, productId]) // 移除 hasFeature 依賴，在函數內部檢查

  useEffect(() => {
    if (!productId && !initializedRef.current) {
      initializedRef.current = true
      fetchEmployees()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // 只在組件掛載時執行一次

  // 抽成獨立函式，讓「儲存後留在原頁」時也能重新載入最新資料
  const fetchProduct = useCallback(async () => {
    if (!productId) return
    setFetching(true)
    try {
      const res = await getInventoryProduct(productId)
      const product = res.data?.data
      setInitialData(product)
      setIsActive(product.status === 'active')
      // 過濾掉默認圖片和占位符圖片，只顯示真實上傳的圖片
      const DEFAULT_IMAGE_URL =
        'https://f005.backblazeb2.com/file/beautyapp/public/assets/samplebox.png'
      const PLACEHOLDER_IMAGE_URL =
        'https://via.placeholder.com/400x400?text=No+Image'
      const realImages =
        product.images?.filter((url) => {
          if (!url) return false
          const fullUrl = url.startsWith('http') ? url : `${PHOTO_URL}${url}`
          return (
            fullUrl !== DEFAULT_IMAGE_URL &&
            fullUrl !== PLACEHOLDER_IMAGE_URL
          )
        }) || []

      form.setFieldsValue({
        categoryId:
          product.categoryId ||
          product.category?.id ||
          product.category?._id,
        name: product.name || product.title,
        manufacturer: product.manufacturer,
        capacity: product.capacity,
        suggestedPrice: product.suggestedPrice || product.price,
        cost: product.cost,
        stock: product.stock,
        lowStockThreshold: product.lowStockThreshold || 10,
        unit: product.unit || '件',
        stockByCapacity: !!product.stockByCapacity,
        description: product.description,
        images:
          realImages.length > 0
            ? realImages.map((url, index) => {
                // 提取原始 URL（去掉 PHOTO_URL 前缀）
                const originalUrl = url.startsWith('http')
                  ? url.replace(PHOTO_URL, '')
                  : url
                return {
                  uid: `-${index}`,
                  name: `image-${index}`,
                  status: 'done',
                  url: url.startsWith('http') ? url : `${PHOTO_URL}${url}`,
                  originalUrl: originalUrl // 保存原始 URL 用于删除
                }
              })
            : []
      })
    } catch (error) {
      console.error('Error fetching product:', error)
      // message.error('獲取商品數據失敗')
    } finally {
      setFetching(false)
    }
  }, [productId, form])

  useEffect(() => {
    fetchProduct()
  }, [fetchProduct])

  const handleFormChange = () => {
    if (initialData) {
      const currentValues = form.getFieldsValue()
      const hasChanged =
        JSON.stringify(currentValues) !== JSON.stringify(initialData)
      setHasChanges(hasChanged)
    }
  }

  const handleFinish = async (values) => {
    setLoading(true)
    try {
      // 調試：檢查表單值
      console.log('表單提交值:', values)
      console.log('是否同時進貨:', values.enablePurchase)
      console.log('庫存數量 (原始值):', values.stock)
      console.log('成本:', values.cost)

      const formData = new FormData()

      formData.append('categoryId', values.categoryId)
      formData.append('title', values.name) // 使用 title 以兼容現有 API
      formData.append('name', values.name) // 同時添加 name 以備後端擴充
      if (values.manufacturer) {
        formData.append('manufacturer', values.manufacturer)
      }
      if (values.capacity) {
        formData.append('capacity', values.capacity)
      }
      // 一律送出（含 false），否則關閉開關時後端收不到、會維持舊值
      formData.append('stockByCapacity', values.stockByCapacity ? 'true' : 'false')
      formData.append('price', values.suggestedPrice || 0) // 使用 price 以兼容現有 API
      formData.append('suggestedPrice', values.suggestedPrice || 0) // 同時添加 suggestedPrice
      // 如果有财务权限，使用用户输入的成本；否则使用商品原有成本或默认值 0
      const costValue = canViewFinance
        ? values.cost || 0
        : initialData?.cost || 0
      formData.append('cost', costValue)

      // 如果選擇同時進貨，庫存數量在入庫日期增加，創建時設為0
      // 如果僅新增商品，使用用戶輸入的庫存數量
      // 確保正確獲取 stock 值（可能是字符串或數字）
      const stockValue =
        values.stock !== undefined && values.stock !== null
          ? Number(values.stock)
          : 0
      const initialStock = values.enablePurchase ? 0 : stockValue

      console.log('庫存處理:', {
        stockValue,
        enablePurchase: values.enablePurchase,
        initialStock
      })

      formData.append('stock', initialStock)
      // 編輯模式且庫存被改動：帶上調整原因/備註，後端會寫入異動明細
      if (
        productId &&
        initialData &&
        Number(stockValue) !== Number(initialData.stock)
      ) {
        formData.append(
          'stockChangeReason',
          values.stockChangeReason || '商品編輯調整庫存'
        )
        if (values.stockChangeNote) {
          formData.append('stockChangeNote', values.stockChangeNote)
        }
      }
      formData.append('lowStockThreshold', values.lowStockThreshold || 10)
      formData.append('unit', values.unit || '件')
      formData.append('status', isActive ? 'active' : 'inactive')
      if (values.description) {
        formData.append('description', values.description)
      }

      // 處理圖片
      if (productId) {
        // 編輯模式：處理圖片刪除
        console.log('刪除的圖片列表:', deletedImages)
        if (deletedImages.length > 0) {
          // 將刪除的圖片 URL 添加到 deleteImages 參數（JSON 字符串格式）
          const deleteImagesJson = JSON.stringify(deletedImages)
          console.log('添加 deleteImages:', deleteImagesJson)
          formData.append('deleteImages', deleteImagesJson)
        }

        // 編輯模式：處理新圖片上傳（使用 files 字段）
        if (values.images && values.images.length > 0) {
          let newImageCount = 0
          values.images.forEach((image) => {
            // 只上傳新選擇的圖片（有 originFileObj 的）
            if (image.originFileObj) {
              console.log('添加新圖片:', image.name)
              formData.append('files', image.originFileObj)
              newImageCount++
            }
          })
          console.log('新上傳的圖片數量:', newImageCount)
        }
      } else {
        // 新增模式：使用 images 字段上傳圖片
        const DEFAULT_IMAGE_URL =
          'https://f005.backblazeb2.com/file/beautyapp/public/assets/samplebox.png'
        const PLACEHOLDER_IMAGE_URL =
          'https://via.placeholder.com/400x400?text=No+Image'
        if (values.images && values.images.length > 0) {
          values.images.forEach((image) => {
            if (image.originFileObj) {
              // 新上傳的圖片
              formData.append('images', image.originFileObj)
            } else if (image.url) {
              // 已存在的圖片，但排除默認圖片和占位符圖片
              const imageUrl = image.url.startsWith('http')
                ? image.url
                : `${PHOTO_URL}${image.url}`
              if (
                imageUrl !== DEFAULT_IMAGE_URL &&
                imageUrl !== PLACEHOLDER_IMAGE_URL
              ) {
                formData.append('existingImages', image.url)
              }
            }
          })
        }
      }

      let createdProductId = null

      if (productId) {
        // 編輯模式：重置刪除列表（提交後清空）
        await updateInventoryProduct(productId, formData)
        message.success('商品更新成功')
        setDeletedImages([]) // 清空刪除列表

        // 編輯模式儲存後留在原頁（不跳回商品列表）。
        // 需重新載入：新上傳的圖片要換成後端回傳的正式網址，
        // 否則再按一次儲存會把同一張圖重複上傳。
        await fetchProduct()

        // 庫存調整原因是一次性的，存檔後清掉，
        // 避免下次再改庫存時沿用上一次的理由
        form.setFieldsValue({
          stockChangeReason: undefined,
          stockChangeNote: undefined
        })
        setHasChanges(false)
      } else {
        const createResponse = await createInventoryProduct(formData)

        // 調試：檢查創建響應
        console.log('商品創建響應:', createResponse)
        console.log('響應數據:', createResponse.data)

        // 獲取創建的商品ID - 嘗試多種可能的響應格式
        createdProductId =
          createResponse.data?.data?._id ||
          createResponse.data?.data?.id ||
          createResponse.data?._id ||
          createResponse.data?.id ||
          createResponse._id ||
          createResponse.id

        console.log('獲取到的商品ID:', createdProductId)

        if (!createdProductId) {
          console.error('無法獲取商品ID，響應結構:', createResponse)
          message.warning('商品創建成功，但無法獲取商品ID，進貨記錄將無法創建')
        } else {
          message.success('商品新增成功')
        }

        // 如果選擇了同時進貨，創建進貨記錄
        if (values.enablePurchase) {
          if (!createdProductId) {
            message.error('無法創建進貨記錄：商品ID未獲取')
            return
          }

          console.log('準備創建進貨記錄，商品ID:', createdProductId)
          try {
            // 計算進貨總價（數量 × 單價）
            const quantity = Number(values.stock) || 0
            // 如果有财务权限，使用用户输入的成本；否则使用商品原有成本或默认值 0
            const unitPrice = canViewFinance
              ? Number(values.cost) || 0
              : initialData?.cost || 0
            const calculatedTotal = quantity * unitPrice

            // 實際付款金額（如果有折扣或其他調整，可以在這裡修改）
            // 目前先使用計算總價，後續可以添加實際付款金額欄位
            const actualPaymentAmount = values.purchaseActualAmount
              ? Number(values.purchaseActualAmount)
              : calculatedTotal

            console.log('進貨數據:', {
              quantity,
              unitPrice,
              calculatedTotal,
              actualPaymentAmount
            })

            const purchaseData = {
              employeeId: values.purchaseEmployeeId,
              employeeName: employees.find(
                (e) => e._id === values.purchaseEmployeeId
              )?.name,
              paymentMethod: values.purchasePaymentMethod, // 'cash' 或 'nonCash'
              stockDate: values.purchaseStockDate.format('YYYY-MM-DD'), // 入庫日期
              paymentDate: values.purchasePaymentDate.format('YYYY-MM-DD'), // 付款日期
              items: [
                {
                  productId: createdProductId,
                  productName: values.name,
                  quantity: quantity,
                  unitPrice: unitPrice,
                  totalPrice: calculatedTotal
                }
              ],
              totalAmount: calculatedTotal, // 計算總價
              actualPaymentAmount: actualPaymentAmount, // 實際付款金額
              note: values.purchaseNote || '新增商品時同時進貨'
            }

            console.log('提交進貨數據:', purchaseData)
            console.log('進貨API端點: /api/store/inventory/purchase')

            const purchaseResponse = await api.post(
              `/api/store/inventory/purchase`,
              purchaseData
            )

            console.log('進貨API響應:', purchaseResponse)
            message.success('進貨記錄建立成功')
          } catch (error) {
            console.error('創建進貨記錄失敗:', error)
            console.error('錯誤詳情:', {
              message: error.message,
              response: error.response?.data,
              status: error.response?.status
            })
            message.warning(
              '商品已創建，但進貨記錄建立失敗：' +
                (error.response?.data?.message || error.message)
            )
          }
        } else {
          console.log('未選擇同時進貨:', {
            enablePurchase: values.enablePurchase,
            createdProductId
          })
        }

        // 只有「新增」才跳回商品列表；編輯模式留在原頁
        navigate(backTo)
      }
    } catch (error) {
      console.error('Error:', error)
      const errorMessage = error.response?.data?.message || '操作失敗'
      message.error(errorMessage)
    }
    setLoading(false)
  }

  const handleStatusChange = (checked) => {
    setIsActive(checked)
    setHasChanges(true)
  }

  const handleLeave = () => {
    if (hasChanges) {
      return window.confirm('您有未保存的更改，確定要離開嗎？')
    }
    return true
  }

  const uploadProps = {
    beforeUpload: async (file) => {
      const isAllowedType = /\.(jpeg|jpg|png|gif|heic|heif)$/i.test(file.name)
      const isLt10M = file.size / 1024 / 1024 < 10

      if (!isLt10M) {
        message.error('圖片必須小於 10MB！')
        return Upload.LIST_IGNORE
      }

      const currentImages = form.getFieldValue('images') || []
      if (currentImages.length >= 10) {
        message.error('最多只能上傳 10 張圖片')
        return Upload.LIST_IGNORE
      }

      // HEIC/HEIF 格式直接上傳，由後端處理（參考 SettingsPage.js 的方法）
      if (/\.(heic|heif)$/i.test(file.name)) {
        // 直接返回 false，允許上傳原始文件
        return false
      }

      if (!isAllowedType) {
        message.error('只能上傳 JPG/PNG/GIF/HEIC/HEIF 格式的圖片！')
        return Upload.LIST_IGNORE
      }

      return false
    },
    multiple: true,
    maxCount: 10,
    listType: 'picture-card',
    accept: 'image/*',
    customRequest: ({ file, onSuccess }) => {
      setTimeout(() => {
        onSuccess('ok')
      }, 0)
    }
  }

  const handleUploadChange = ({ fileList: newFileList }) => {
    // 處理文件列表（參考 SettingsPage.js 的方法）
    const updatedFileList = newFileList.map((file) => {
      // 如果文件是 HEIC/HEIF 格式，更新其 type 和 name（僅用於顯示，實際文件仍為 HEIC 格式）
      if (file.originFileObj && /\.(heic|heif)$/i.test(file.name)) {
        return {
          ...file,
          type: 'image/jpeg',
          name: file.name.replace(/\.(heic|heif)$/i, '.jpg')
        }
      }
      return file
    })

    const limitedFileList = updatedFileList.slice(0, 10)
    form.setFieldsValue({ images: limitedFileList })
    handleFormChange()
  }

  // 處理圖片刪除
  const handleImageRemove = (file) => {
    console.log('刪除圖片:', file)
    console.log('原始 URL:', file.originalUrl)

    // 如果是已存在的圖片（有 originalUrl），添加到刪除列表
    if (file.originalUrl) {
      setDeletedImages((prev) => {
        // 避免重複添加
        if (!prev.includes(file.originalUrl)) {
          const newList = [...prev, file.originalUrl]
          console.log('更新刪除列表:', newList)
          return newList
        }
        return prev
      })
    } else {
      console.warn('刪除的圖片沒有 originalUrl，可能是新上傳的圖片')
    }
    setHasChanges(true)
    return true // 返回 true 允許刪除
  }

  if (fetching) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size='large' />
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <HeaderSection>
        <Breadcrumb
          items={[
            {
              // 跟著 backTo 走，不要寫死 —— 從線上商店的上架商品頁進來時，
              // 寫死會把人丟到庫存管理的第一頁，與返回鍵的行為不一致
              title: (
                <a onClick={() => navigate(backTo)}>
                  {backTo.startsWith('/shop/products')
                    ? '上架商品'
                    : '庫存管理'}
                </a>
              )
            },
            { title: productId ? '編輯商品' : '新增/採購商品' }
          ]}
        />
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => {
            if (handleLeave()) {
              goBack()
            }
          }}
        >
          返回列表
        </Button>
      </HeaderSection>

      {productId && (
        <StatusSwitch>
          <span>商品狀態：</span>
          <Switch
            checked={isActive}
            onChange={handleStatusChange}
            checkedChildren='啟用'
            unCheckedChildren='停用'
          />
          <StatusText isActive={isActive}>
            {isActive ? '商品已啟用' : '商品已停用'}
          </StatusText>
        </StatusSwitch>
      )}

      <StyledForm
        form={form}
        layout='vertical'
        onFinish={handleFinish}
        onValuesChange={handleFormChange}
      >
        <ContentCard title='基本信息'>
          <Form.Item
            label='商品分類'
            extra={
              categories.length === 0
                ? '尚無分類時無法選擇，請先點「新增分類」建立分類。'
                : undefined
            }
          >
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                width: '100%',
                flexWrap: 'wrap'
              }}
            >
              <Form.Item
                name='categoryId'
                noStyle
                rules={[{ required: true, message: '請選擇商品分類' }]}
                style={{ flex: 1, minWidth: 200, marginBottom: 0 }}
              >
                <Select
                  placeholder={
                    categories.length === 0
                      ? '請先新增分類'
                      : '請選擇分類'
                  }
                  disabled={categories.length === 0}
                  style={{ width: '100%' }}
                >
                  {categories.map((category) => (
                    <Option key={category._id} value={category._id}>
                      {category.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Button
                type='default'
                icon={<PlusOutlined />}
                style={{ flexShrink: 0 }}
                onClick={() => setCategoryModalOpen(true)}
              >
                新增分類
              </Button>
            </div>
          </Form.Item>

          <Form.Item
            name='name'
            label='商品名稱'
            rules={[{ required: true, message: '請輸入商品名稱' }]}
          >
            <Input placeholder='請輸入商品名稱' />
          </Form.Item>

          <Form.Item name='manufacturer' label='廠商'>
            <Input placeholder='請輸入廠商名稱（可選）' />
          </Form.Item>

          <Form.Item name='capacity' label='容量'>
            <Input placeholder='例如：30ml, 50g（可選）' />
          </Form.Item>

          {/* 庫存記總量（例如 ml）、販售以罐為單位的商品用這個開關。
              預設關閉 —— 多數店家的庫存數字本來就是件數，
              若自動換算會讓他們的庫存一夕之間變成幾分之一。 */}
          <Form.Item
            name='stockByCapacity'
            label='庫存以容量計算'
            valuePropName='checked'
            extra='開啟後，庫存數字視為「總容量」，販售時以上方「容量」換算成罐數。例如庫存 41、容量 8ml → 可售 5 罐，賣一罐扣 8。庫存不足一罐時前台顯示已售完。'
          >
            <Switch />
          </Form.Item>

          {stockByCapacity && (
            <div
              style={{
                marginTop: -8,
                marginBottom: 16,
                padding: '10px 14px',
                borderRadius: 8,
                background: parsedUnitSize ? '#f6ffed' : '#fff2f0',
                border: `1px solid ${parsedUnitSize ? '#b7eb8f' : '#ffccc7'}`,
                fontSize: 13,
                lineHeight: 1.7,
                color: parsedUnitSize ? '#389e0d' : '#cf1322'
              }}
            >
              {parsedUnitSize ? (
                <>
                  解析結果：一罐 <strong>{parsedUnitSize}</strong>
                  {'　'}目前庫存 <strong>{Number(stock) || 0}</strong>
                  {'　'}→ 可售 <strong>{sellableUnits}</strong> 罐
                  {sellableUnits === 0 && '（不足一罐，前台顯示已售完）'}
                </>
              ) : (
                <>
                  「容量」欄位讀不出數字，換算不會生效（會照舊 1 件扣 1）。
                  請填成像「8ml」這樣含數字的格式。
                </>
              )}
            </div>
          )}

          <Form.Item
            name='unit'
            label='單位'
            initialValue='件'
            extra='「單位」= 庫存／進貨／消耗時的計量依據（數字皆與它一致）。選 ml、g、kg 時，庫存與進貨「數量」即為該單位之總量。若以瓶進貨、偶爾按用量扣帳，建議單位選「瓶」、容量填規格（如 500ml），消耗可填小數瓶數。'
          >
            <Select placeholder='請選擇單位'>
              <Option value='件'>件</Option>
              <Option value='瓶'>瓶</Option>
              <Option value='盒'>盒</Option>
              <Option value='支'>支</Option>
              <Option value='包'>包</Option>
              <Option value='組'>組</Option>
              <Option value='ml'>ml（毫升）</Option>
              <Option value='g'>g（公克）</Option>
              <Option value='kg'>kg（公斤）</Option>
            </Select>
          </Form.Item>

          <ImagePreview>
            <Form.Item
              name='images'
              label='商品圖片'
              valuePropName='fileList'
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                  return e
                }
                return e && e.fileList
              }}
              extra='支持多張圖片上傳，最多10張，每張大小不超過10MB'
            >
              <Upload
                {...uploadProps}
                onChange={handleUploadChange}
                onRemove={handleImageRemove}
              >
                {(!form.getFieldValue('images') ||
                  form.getFieldValue('images').length < 10) && (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>上傳</div>
                  </div>
                )}
              </Upload>
            </Form.Item>
          </ImagePreview>

          <Form.Item
            name='description'
            label='商品描述'
            extra='換行會照實顯示在前台商品頁，可以分行寫成分「成分」「用法」「保存期限」等段落。'
          >
            <Input.TextArea
              rows={6}
              autoSize={{ minRows: 6, maxRows: 16 }}
              placeholder={'請輸入商品描述（可選）\n可直接按 Enter 換行，前台會照原樣顯示'}
            />
          </Form.Item>
        </ContentCard>

        <ContentCard title='價格與庫存'>
          <PriceGroup>
            <Form.Item
              name='suggestedPrice'
              label='建議售價'
              rules={[{ required: true, message: '請輸入建議售價' }]}
            >
              <InputNumber
                min={0}
                step={0.01}
                precision={2}
                prefix='NT$'
                style={{ width: '100%' }}
                placeholder='請輸入建議售價'
              />
            </Form.Item>

            {canViewFinance && (
              <Form.Item
                name='cost'
                label='成本'
                rules={[{ required: true, message: '請輸入成本' }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  prefix='NT$'
                  style={{ width: '100%' }}
                  placeholder='請輸入成本'
                />
              </Form.Item>
            )}

            <Form.Item
              name='stock'
              label={enablePurchase ? '進貨數量' : '庫存數量'}
              rules={[{ required: true, message: '請輸入數量' }]}
              tooltip={
                enablePurchase
                  ? '進貨數量將在入庫日期增加到商品庫存中'
                  : '商品的初始庫存數量'
              }
            >
              <InputNumber
                min={0}
                precision={0}
                style={{ width: '100%' }}
                placeholder={
                  enablePurchase ? '請輸入進貨數量' : '請輸入庫存數量'
                }
              />
            </Form.Item>

            {/* 編輯模式改動庫存時，要求填寫原因（會寫入該商品的異動明細，方便追溯） */}
            {stockChanged && (
              <>
                <Form.Item
                  name='stockChangeReason'
                  label='調整原因'
                  rules={[
                    { required: true, message: '請填寫庫存調整原因' }
                  ]}
                  tooltip='你直接改了庫存數量，請說明原因。此調整會記錄到該商品的「異動明細」中可供追溯。'
                  extra={`庫存將由 ${initialData?.stock ?? '-'} 調整為 ${
                    stock ?? '-'
                  }`}
                >
                  <Input placeholder='例如：盤點調整、商品報廢、贈品入庫' />
                </Form.Item>
                <Form.Item name='stockChangeNote' label='調整備註（選填）'>
                  <Input placeholder='補充說明（選填）' />
                </Form.Item>
              </>
            )}

            <Form.Item
              name='lowStockThreshold'
              label='低庫存閾值'
              initialValue={10}
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder='庫存低於此值時提醒'
              />
            </Form.Item>

            {/* 小計顯示 - 只有有财务权限才显示 */}
            {canViewFinance && (
              <SubtotalDisplay>
                <span className='subtotal-label'>小計（庫存數量 × 成本）</span>
                <span className='subtotal-value'>
                  NT$ {subtotal.toLocaleString()}
                </span>
              </SubtotalDisplay>
            )}
          </PriceGroup>
        </ContentCard>

        {/* 進貨選項 - 只在新增商品時顯示，且需要有财务权限 */}
        {!productId && canViewFinance && (
          <ContentCard title='進貨選項'>
            <Form.Item
              name='enablePurchase'
              valuePropName='checked'
              initialValue={false}
            >
              <Switch
                checkedChildren='同時進貨'
                unCheckedChildren='僅新增商品'
              />
            </Form.Item>

            {enablePurchase && (
              <Row gutter={[24, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name='purchaseEmployeeId'
                    label='經手人'
                    rules={[
                      {
                        required: enablePurchase,
                        message: '請選擇經手人'
                      }
                    ]}
                  >
                    <Select placeholder='請選擇員工' showSearch>
                      {employees.map((employee) => (
                        <Option key={employee._id} value={employee._id}>
                          {employee.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name='purchasePaymentMethod'
                    label='購買方式'
                    rules={[
                      {
                        required: enablePurchase,
                        message: '請選擇購買方式'
                      }
                    ]}
                  >
                    <Select placeholder='請選擇購買方式'>
                      <Option value='cash'>現金</Option>
                      <Option value='nonCash'>非現金</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name='purchaseStockDate'
                    label='入庫日期'
                    tooltip='商品實際入庫的日期，用於庫存增加'
                    rules={[
                      {
                        required: enablePurchase,
                        message: '請選擇入庫日期'
                      }
                    ]}
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      format='YYYY-MM-DD'
                      placeholder='請選擇入庫日期'
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name='purchasePaymentDate'
                    label='付款日期'
                    tooltip='實際付款的日期，用於採購支出記錄'
                    rules={[
                      {
                        required: enablePurchase,
                        message: '請選擇付款日期'
                      }
                    ]}
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      format='YYYY-MM-DD'
                      placeholder='請選擇付款日期'
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name='purchaseActualAmount'
                    label='實際付款金額'
                    tooltip='自動計算：庫存數量 × 成本'
                    rules={[
                      {
                        required: enablePurchase,
                        message: '實際付款金額為必填'
                      }
                    ]}
                  >
                    <InputNumber
                      min={0}
                      precision={0}
                      prefix='NT$'
                      style={{ width: '100%' }}
                      disabled
                      readOnly
                    />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item name='purchaseNote' label='進貨備註'>
                    <Input.TextArea
                      rows={2}
                      placeholder='請輸入進貨備註（可選）'
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {enablePurchase && canViewFinance && (
              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  background: '#f0f9ff',
                  borderRadius: 8,
                  fontSize: 13,
                  color: '#0369a1'
                }}
              >
                <strong>提示：</strong>
                選擇「同時進貨」後，商品創建成功時會自動建立進貨記錄，庫存數量將在入庫日期增加，採購支出將在付款日期記錄。
                <br />
                <strong>計算總價：</strong>
                {(() => {
                  const quantity = Number(form.getFieldValue('stock')) || 0
                  const cost = Number(form.getFieldValue('cost')) || 0
                  const total = quantity * cost
                  return `進貨數量 ${quantity} × 成本 ${cost} = ${total.toLocaleString()} 元`
                })()}
              </div>
            )}
          </ContentCard>
        )}

        <ButtonGroup>
          <Popconfirm
            title='確定要取消嗎？'
            description='取消後所有更改都將丟失'
            onConfirm={() => navigate(backTo)}
            okText='確定'
            cancelText='取消'
            disabled={!hasChanges}
          >
            <Button size='large' disabled={!hasChanges}>
              取消
            </Button>
          </Popconfirm>
          <Button
            type='primary'
            size='large'
            htmlType='submit'
            loading={loading}
            disabled={!hasChanges && productId}
          >
            {productId ? '保存修改' : '確認新增'}
          </Button>
        </ButtonGroup>
      </StyledForm>

      <Modal
        title='新增商品分類'
        open={categoryModalOpen}
        onCancel={() => setCategoryModalOpen(false)}
        footer={null}
        destroyOnClose
        width={480}
      >
        <InventoryCategoryForm
          onSuccess={handleCategoryCreated}
          onCancel={() => setCategoryModalOpen(false)}
        />
      </Modal>
    </PageContainer>
  )
}

export default AddInventoryProductPage
